import { AlertTriangle, ArrowDownToLine } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { Suggerimento } from "@/components/panoramica/Suggerimento";
import { cn } from "@/lib/utils";

export type TonoNumero = "forte" | "normale" | "perdita" | "info";

export interface CellaNumeroProps {
  valore: string;
  // Il numero vero: zero diventa grigio qualunque sia il tono.
  numero: number | null;
  href?: string;
  tono?: TonoNumero;
  avviso?: boolean;
  variazione?: string | null;
  sotto?: string | null;
  hrefSotto?: string;
  sottoAuto?: boolean;
  iconaFloor?: boolean;
  suggerimento?: { titolo: string; testo: ReactNode } | null;
  totale?: boolean;
}

const COLORE: Record<TonoNumero | "muto", string> = {
  forte: "text-ink",
  normale: "text-ink-2",
  muto: "text-ink-3",
  perdita: "text-danger",
  info: "text-info",
};

const LINK = "no-underline hover:underline focus-visible:underline";

export function CellaNumero({
  valore,
  numero,
  href,
  tono = "forte",
  avviso,
  variazione,
  sotto,
  hrefSotto,
  sottoAuto,
  iconaFloor,
  suggerimento,
  totale,
}: CellaNumeroProps): JSX.Element {
  const classi = cn(
    "font-mono text-data tabular-nums leading-tight",
    COLORE[numero === 0 ? "muto" : tono],
    totale && "font-semibold",
  );
  const numeroEl = href ? (
    <Link to={href} onClick={(e) => e.stopPropagation()} className={cn(classi, LINK)}>
      {valore}
    </Link>
  ) : !href && suggerimento ? (
    <span
      tabIndex={0}
      className={cn(
        classi,
        "cursor-help focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink",
      )}
    >
      {valore}
    </span>
  ) : (
    <span className={classi}>{valore}</span>
  );
  const numero_wrapped = suggerimento ? (
    <Suggerimento titolo={suggerimento.titolo} testo={suggerimento.testo} allinea="destra">
      {numeroEl}
    </Suggerimento>
  ) : (
    numeroEl
  );
  const riga = (
    <span className="flex items-baseline justify-end gap-1">
      {avviso ? (
        <AlertTriangle className="h-[11px] w-[11px] shrink-0 self-center text-danger" strokeWidth={1.9} role="img" aria-label="da guardare" />
      ) : null}
      {numero_wrapped}
      {variazione ? (
        <span className="text-micro tabular-nums text-ink-3">
          {variazione}
          <span className="sr-only"> rispetto a ieri</span>
        </span>
      ) : null}
    </span>
  );
  const sottoClassi = cn(
    "mt-0.5 flex items-center justify-end gap-1 whitespace-nowrap text-right text-micro tabular-nums",
    sottoAuto ? "text-accent" : "text-ink-3",
  );
  const sottoContenuto = (
    <>
      {sotto}
      {iconaFloor ? (
        <ArrowDownToLine className="h-3 w-3 text-warn" strokeWidth={2} role="img" aria-label="attaccate al floor" />
      ) : null}
    </>
  );
  return (
    <div className="flex flex-col items-end">
      {riga}
      {sotto ? (
        hrefSotto ? (
          <Link to={hrefSotto} onClick={(e) => e.stopPropagation()} className={cn(sottoClassi, LINK)}>
            {sottoContenuto}
          </Link>
        ) : (
          <span className={sottoClassi}>{sottoContenuto}</span>
        )
      ) : null}
    </div>
  );
}
